import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Tag as TagIcon, Trash2 } from 'lucide-react';
import type { Tag, TagsResponse } from '@lira/shared';
import { apiDelete, apiGet } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { toast } from '@/stores/toast';
import { ConfirmDialog, EmptyState, Skeleton } from '@/components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { TagFormModal } from './TagFormModal';

function TagRow({
  tag,
  onEdit,
  onDelete,
}: {
  tag: Tag;
  onEdit: (t: Tag) => void;
  onDelete: (t: Tag) => void;
}) {
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <span className="grid size-9 shrink-0 place-items-center rounded-full bg-primary-weak text-primary">
        <TagIcon className="size-4" />
      </span>
      <span className="min-w-0 flex-1 truncate text-[15px] text-ink">{tag.name}</span>
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={() => onEdit(tag)}
          aria-label="Editar tag"
          title="Editar"
          className="grid size-8 place-items-center rounded-full text-muted transition-colors hover:bg-canvas hover:text-ink"
        >
          <Pencil className="size-4" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(tag)}
          aria-label="Excluir tag"
          title="Excluir"
          className="grid size-8 place-items-center rounded-full text-muted transition-colors hover:bg-expense-weak hover:text-expense"
        >
          <Trash2 className="size-4" />
        </button>
      </div>
    </li>
  );
}

export function TagsPage() {
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Tag | undefined>(undefined);
  const [deleting, setDeleting] = useState<Tag | null>(null);

  const query = useQuery({
    queryKey: ['tags'],
    queryFn: () => apiGet<TagsResponse>('/tags'),
  });

  const removeTag = useMutation({
    mutationFn: (id: string) => apiDelete(`/tags/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tags'] });
      setDeleting(null);
      toast('Tag excluída.', 'success');
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const tags = query.data?.tags ?? [];
  const isEmpty = query.isSuccess && tags.length === 0;

  const openNew = () => {
    setEditing(undefined);
    setFormOpen(true);
  };
  const openEdit = (tag: Tag) => {
    setEditing(tag);
    setFormOpen(true);
  };

  return (
    <section className="space-y-6">
      <PageHeader
        title="Tags"
        subtitle="Crie marcadores transversais para seus lançamentos."
        actions={
          <button
            type="button"
            onClick={openNew}
            className="inline-flex min-h-11 items-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-primary-hover"
          >
            <Plus className="size-5" />
            Nova tag
          </button>
        }
      />

      {query.isLoading && (
        <div className="space-y-2 rounded-[var(--radius-panel)] bg-surface p-4 shadow-card">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="line" className="h-10" />
          ))}
        </div>
      )}

      {query.isError && (
        <p className="rounded-lg bg-expense-weak px-4 py-6 text-center text-sm text-expense">
          {errorMessage(query.error)}
        </p>
      )}

      {isEmpty && (
        <EmptyState
          icon={TagIcon}
          title="Nenhuma tag ainda"
          description="Tags ajudam a marcar lançamentos de forma transversal às categorias."
        >
          <button
            type="button"
            onClick={openNew}
            className="inline-flex h-11 items-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-primary-hover"
          >
            <Plus className="size-5" />
            Nova tag
          </button>
        </EmptyState>
      )}

      {tags.length > 0 && (
        <ul className="divide-y divide-border overflow-hidden rounded-[var(--radius-panel)] bg-surface shadow-card">
          {tags.map((tag) => (
            <TagRow key={tag.id} tag={tag} onEdit={openEdit} onDelete={setDeleting} />
          ))}
        </ul>
      )}

      <TagFormModal open={formOpen} onClose={() => setFormOpen(false)} tag={editing} />

      <ConfirmDialog
        open={deleting != null}
        title="Excluir tag"
        message={`Tem certeza que deseja excluir a tag "${deleting?.name ?? ''}"? Esta ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        loading={removeTag.isPending}
        onConfirm={() => deleting && removeTag.mutate(deleting.id)}
        onCancel={() => setDeleting(null)}
      />
    </section>
  );
}
